export interface AnalyticsUserConfig {
  userId: string;
  email: string;
  isInternal: boolean;
  accessType: string;
  generatorId: string | number | null;
  generatorName: string | null;
}

const isAnalyticsEnabled = (): boolean =>
  import.meta.env.VITE_GA4_ENABLED === "true";

const getGtag = (): Window["gtag"] | null => {
  if (typeof window === "undefined") return null;
  if (!isAnalyticsEnabled()) return null;
  if (typeof window.gtag !== "function") return null;

  return window.gtag;
};

export const hashEmail = async (email: string): Promise<string> => {
  const data = new TextEncoder().encode(email.trim().toLowerCase());
  const digest = await crypto.subtle.digest("SHA-256", data);

  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
};

export const initAnalytics = (config: AnalyticsUserConfig): void => {
  const gtag = getGtag();
  if (!gtag) return;

  gtag("set", {
    user_id: config.userId,
  });

  gtag("set", "user_properties", {
    user_type: config.isInternal ? "musa_internal" : "generator_client",
    access_type: config.accessType,
    generator_id:
      config.generatorId != null ? String(config.generatorId) : undefined,
    generator_name: config.generatorName ?? undefined,
    environment: import.meta.env.VITE_ENV,
  });
};

export const trackEvent = (
  name: string,
  params?: Record<string, unknown>,
): void => {
  const gtag = getGtag();
  if (!gtag) return;

  gtag("event", name, params);
};

export const trackPageView = (path: string, title?: string): void => {
  trackEvent("page_view", { page_path: path, page_title: title });
};

export const updateConsent = (granted: boolean): void => {
  const gtag = getGtag();
  if (!gtag) return;

  gtag("consent", "update", {
    analytics_storage: granted ? "granted" : "denied",
  });
};
